import React from 'react';
import {
    Heart,
    Calendar,
    Users,
    MessageCircle,
    Sparkles,
    CheckCircle,
    ArrowRight,
    Wine,
    Clock,
    MapPin,
    UserPlus,
    Star,
    Shield,
    Award
} from 'lucide-react';

const EventsPage = ({ onJoinCommunity }) => {
    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative pt-32 pb-32 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                            <Calendar size={18} className="text-indigo-400" />
                            <span>Affinity Club · Evenimente</span>
                        </div>

                        {/* Main Title - FĂRĂ GRADIENT */}
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                            Evenimente pentru cupluri &{' '}
                            <span className="text-white">
                                membri Affinity Club
                            </span>
                        </h1>

                        {/* Description */}
                        <p className="text-xl md:text-2xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto font-light">
                            Pregătim experiențe premium dedicate conexiunilor reale: seri pentru cupluri,
                            întâlniri ghidate, discuții profunde și evenimente special create pentru comunitatea Affinity Club.
                        </p>

                        {/* Coming Soon Badge */}
                        <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-md border border-amber-500/30 px-6 py-3 rounded-full text-lg font-semibold text-amber-400 shadow-lg">
                            <Sparkles size={20} />
                            <span>În curând</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ce vei putea descoperi */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Ce vei putea descoperi în curând</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Evenimente exclusive, create cu grijă pentru membrii comunității Affinity Club
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Event Type 1 */}
                        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 hover:shadow-2xl transition-all group">
                            <div className="w-16 h-16 bg-rose-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Heart className="text-white" size={32} fill="currentColor" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-4">Seri dedicate cuplurilor</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                Exerciții ghidate, dialog emoțional și reconectare. Sesiuni intime create pentru a adânci conexiunea dintre parteneri.
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-rose-600 mr-2" size={16} />
                                    Exerciții ghidate de psihoterapeuți
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-rose-600 mr-2" size={16} />
                                    Dialog emoțional profund
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-rose-600 mr-2" size={16} />
                                    Tehnici de reconectare
                                </div>
                            </div>
                        </div>

                        {/* Event Type 2 */}
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border-2 border-purple-200 hover:shadow-2xl transition-all group">
                            <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Wine className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-4">Evenimente elegante</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                În restaurante selectate, cu atmosferă intimă și rafinată. Experiențe premium pentru conexiuni autentice.
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-purple-600 mr-2" size={16} />
                                    Locații premium selectate
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-purple-600 mr-2" size={16} />
                                    Atmosferă intimă și rafinată
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-purple-600 mr-2" size={16} />
                                    Servicii de înaltă calitate
                                </div>
                            </div>
                        </div>

                        {/* Event Type 3 */}
                        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-200 hover:shadow-2xl transition-all group">
                            <div className="w-16 h-16 bg-cyan-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <MessageCircle className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-4">Întâlniri tematice</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                Pentru membri Affinity Club: conversații curate, dinamici de grup și activități interactive pentru conexiuni autentice.
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-cyan-600 mr-2" size={16} />
                                    Conversații profunde și curate
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-cyan-600 mr-2" size={16} />
                                    Dinamici de grup facilitate
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-cyan-600 mr-2" size={16} />
                                    Activități interactive
                                </div>
                            </div>
                        </div>

                        {/* Event Type 4 */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 hover:shadow-2xl transition-all group">
                            <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Users className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-4">Workshopuri relaționale</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                Create pentru îmbunătățirea comunicării și consolidarea relației. Învață instrumente practice pentru o relație sănătoasă.
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-emerald-600 mr-2" size={16} />
                                    Tehnici de comunicare eficientă
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-emerald-600 mr-2" size={16} />
                                    Exerciții de consolidare
                                </div>
                                <div className="flex items-center text-sm text-slate-600">
                                    <CheckCircle className="text-emerald-600 mr-2" size={16} />
                                    Instrumente practice
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Exclusivitate & Premium */}
            <section className="py-28 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white relative overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-12 border border-white/10 shadow-2xl relative overflow-hidden">
                            {/* Inner blur decorations */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>

                            <div className="relative z-10 text-center">
                                {/* Badge */}
                                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                                    <Star size={18} className="text-amber-400" />
                                    <span>Acces Exclusiv</span>
                                </div>

                                {/* Title */}
                                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                                    Evenimente cu număr limitat de locuri
                                </h2>

                                {/* Description */}
                                <p className="text-xl text-slate-300 mb-10 opacity-90 leading-relaxed font-light max-w-2xl mx-auto">
                                    Evenimentele vor avea <strong className="text-white font-semibold">număr limitat de locuri</strong>, dedicate exclusiv
                                    membrilor activi ai comunității Affinity Club.
                                </p>

                                {/* Feature Cards */}
                                <div className="grid md:grid-cols-3 gap-6 mb-10">
                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-indigo-400/50 transition-all duration-300">
                                        <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <UserPlus className="text-white" size={28} />
                                        </div>
                                        <div className="text-lg font-semibold text-white">Doar membri activi</div>
                                    </div>
                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-emerald-400/50 transition-all duration-300">
                                        <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <Shield className="text-white" size={28} />
                                        </div>
                                        <div className="text-lg font-semibold text-white">Mediu sigur & verificat</div>
                                    </div>
                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-amber-400/50 transition-all duration-300">
                                        <div className="w-14 h-14 bg-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <Award className="text-white" size={28} />
                                        </div>
                                        <div className="text-lg font-semibold text-white">Experiențe premium</div>
                                    </div>
                                </div>

                                {onJoinCommunity && (
                                    <button
                                        onClick={onJoinCommunity}
                                        className="group bg-white text-slate-900 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-3"
                                    >
                                        Alătură-te comunității
                                        <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Visual Preview */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Atmosferă & Experiență</h2>
                        <p className="text-xl text-slate-600">
                            Evenimente create pentru conexiuni autentice într-un cadru elegant
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                            <img
                                src="https://images.pexels.com/photos/262047/pexels-photo-262047.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Restaurant elegant"
                                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                                <div className="text-white">
                                    <div className="flex items-center mb-2">
                                        <MapPin size={18} className="mr-2" />
                                        <span className="text-sm font-semibold">Locații Premium</span>
                                    </div>
                                    <h3 className="text-xl font-bold">Restaurante Selectate</h3>
                                </div>
                            </div>
                        </div>

                        <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                            <img
                                src="https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Cuplu conversație"
                                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                                <div className="text-white">
                                    <div className="flex items-center mb-2">
                                        <Heart size={18} className="mr-2" />
                                        <span className="text-sm font-semibold">Conexiuni Autentice</span>
                                    </div>
                                    <h3 className="text-xl font-bold">Conversații Profunde</h3>
                                </div>
                            </div>
                        </div>

                        <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                            <img
                                src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Workshop grup"
                                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                                <div className="text-white">
                                    <div className="flex items-center mb-2">
                                        <Users size={18} className="mr-2" />
                                        <span className="text-sm font-semibold">Workshopuri Ghidate</span>
                                    </div>
                                    <h3 className="text-xl font-bold">Activități Interactive</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cum te pregătești */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">Cum te pregătești pentru evenimente</h2>
                            <p className="text-xl text-slate-600">
                                Pași simpli pentru a participa la experiențele Affinity Club
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-rose-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                                    1
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">Devino membru activ</h3>
                                    <p className="text-slate-600">
                                        Înregistrează-te și completează profilul psihologic pentru a face parte din comunitatea Affinity Club.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-rose-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                                    2
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">Primești notificări</h3>
                                    <p className="text-slate-600">
                                        Vei fi notificat când sunt disponibile evenimente noi, cu detalii complete despre fiecare experiență.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-rose-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                                    3
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">Înscrie-te rapid</h3>
                                    <p className="text-slate-600">
                                        Locurile sunt limitate! Când găsești un eveniment care te interesează, înscrie-te rapid pentru a-ți rezerva locul.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-rose-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                                    4
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">Trăiește experiența</h3>
                                    <p className="text-slate-600">
                                        Participă la eveniment cu deschidere și autenticitate. Fiecare experiență este creată pentru conexiuni reale.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter / Stay Updated */}
            <section className="py-28 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white relative overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                            <Clock size={18} className="text-amber-400" />
                            <span>În curând</span>
                        </div>

                        {/* Title */}
                        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                            Fii primul care află când lansăm evenimente
                        </h2>

                        {/* Description */}
                        <p className="text-xl md:text-2xl text-slate-300 mb-10 font-light max-w-2xl mx-auto">
                            Înscrie-te în comunitatea Affinity Club și vei primi notificări când vor fi disponibile primele evenimente.
                        </p>

                        {/* Info Cards */}
                        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 mb-10">
                            <div className="grid md:grid-cols-3 gap-6 mb-8">
                                <div className="text-center">
                                    <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                        <Calendar className="text-white" size={28} />
                                    </div>
                                    <div className="font-semibold text-white mb-1">Vara 2026</div>
                                    <div className="text-sm text-slate-400">Primul eveniment</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                        <Users className="text-white" size={28} />
                                    </div>
                                    <div className="font-semibold text-white mb-1">15-20 participanți</div>
                                    <div className="text-sm text-slate-400">Per eveniment</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                        <MapPin className="text-white" size={28} />
                                    </div>
                                    <div className="font-semibold text-white mb-1">București</div>
                                    <div className="text-sm text-slate-400">Prima locație</div>
                                </div>
                            </div>

                            {onJoinCommunity && (
                                <button
                                    onClick={onJoinCommunity}
                                    className="group bg-white text-slate-900 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-3"
                                >
                                    Vreau să fiu notificat
                                    <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EventsPage;